import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from '../../store/middlewares/createAsyncThunkFromFetch';
import {
  createCompanyVideoAPI,
  deleteCompanyVideoAPI,
  fetchCompanyVideoAPI,
  getCompanyVideosAPI,
  updateCompanyVideoAPI,
} from 'src/features/companyVideos/companyVideos.api';
import { CompanyVideo } from 'src/features/companyVideos/companyVideos.types';

const name = 'companyVideos';

const companyVideosEntityAdapter = createEntityAdapter<AsyncEntity<CompanyVideo>>({
  selectId: item => item.id,
});

export const companyVideosSelectors = companyVideosEntityAdapter.getSelectors();

export const fetchCompanyVideos = createAsyncThunkFromFetch(`${name}/fetchCompanyVideos`, getCompanyVideosAPI);
export const createCompanyVideo = createAsyncThunkFromFetch(`${name}/createCompanyVideo`, createCompanyVideoAPI);
export const fetchCompanyVideo = createAsyncThunkFromFetch(`${name}/fetchCompanyVideo`, fetchCompanyVideoAPI);
export const updateCompanyVideo = createAsyncThunkFromFetch(`${name}/updateCompanyVideo`, updateCompanyVideoAPI);
export const deleteCompanyVideo = createAsyncThunkFromFetch(`${name}/deleteCompanyVideo`, deleteCompanyVideoAPI);

const packagesSlice = createSlice({
  name,
  initialState: companyVideosEntityAdapter.getInitialState<{
    isPending: boolean;
    hasError: boolean;
    companySelector: Record<string, string[]>;
  }>({
    isPending: false,
    hasError: false,
    companySelector: {},
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchCompanyVideos.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyVideos.fulfilled, (state, { payload }) => {
      companyVideosEntityAdapter.setAll(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchCompanyVideos.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteCompanyVideo.pending, (state, { meta }) => {
      companyVideosEntityAdapter.updateOne(state, {
        id: meta.arg.companyId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteCompanyVideo.fulfilled, (state, { meta }) => {
      companyVideosEntityAdapter.removeOne(state, meta.arg.companyId);
    });
    builder.addCase(deleteCompanyVideo.rejected, (state, { meta }) => {
      companyVideosEntityAdapter.updateOne(state, {
        id: meta.arg.companyId,
        changes: { isPending: false, hasError: true },
      });
    });
    builder.addCase(createCompanyVideo.fulfilled, (state, { payload }) => {
      companyVideosEntityAdapter.addOne(state, payload);
    });
    builder.addCase(fetchCompanyVideo.fulfilled, (state, { payload }) => {
      companyVideosEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateCompanyVideo.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(updateCompanyVideo.fulfilled, (state, { payload }) => {
      companyVideosEntityAdapter.updateOne(state, {
        id: payload.id,
        changes: payload,
      });
      state.isPending = false;
    });
  },
});

const { reducer } = packagesSlice;

export default reducer;
